import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest, formatDate } from '../../utils';
import './MemberHome.css';

const fallbackEvent = require('./../../assets/images/fallback/event.png');

/* The host's "Events I'm hosting" screen. For each event the member hosts, how many people
   registered and how many showed up.

   ★ And the ways in, where there are any. An INTERNAL host — one of the organising company's own
   people — runs the event from here and edits it too; an EXTERNAL host we sent in gets the
   editor only, because the date and the room are theirs to get right and the room's own numbers
   by name are not. Both flags are the server's answer to those screens' own gates, so neither
   ever links into a 403. Backend: GET /api/bravos/my-lab. */
const MyLab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/my-lab')
      .then((res) => {
        if (res && res.redirect) { navigate(res.redirect); return; }
        setData(res);
      })
      .catch(() => navigate('/member-home'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const labs = (data && data.labs) || [];

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div>
            <Title my="0" className="mt-1">{t('eventsImHosting')}</Title>
            <p className="bravos-brand__name">{t('eventsImHostingNote')}</p>
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={() => navigate('/member-home')}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading ? (
        <Card><span className="auth__spinner" /></Card>
      ) : labs.length === 0 ? (
        <Card><p className="text-muted mb-0">{t('noEventsHosted')}</p></Card>
      ) : (
        labs.map((l) => (
          <Card key={l.event_id}>
            <div className="bravos-lab__head">
              <span
                className="bravos-event__thumb"
                style={{ backgroundImage: `url("${l.image_link || fallbackEvent}")` }}
              />
              <div>
                <div className="bravos-event__name">{l.event_name}</div>
                <div className="bravos-event__meta">
                  {[l.date ? formatDate(l.date) : null, l.location_name].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
            <div className="bravos-stats mt-2">
              <div className="bravos-stat">
                <span className="bravos-stat__num">{l.registered}</span>
                <span className="bravos-stat__label">{t('registered')}</span>
              </div>
              <div className="bravos-stat">
                <span className="bravos-stat__num">{l.showed_up}</span>
                <span className="bravos-stat__label">{t('showedUp')}</span>
              </div>
            </div>
            {l.can_run || l.can_edit ? (
              <div className="bravos-lab__acts">
                {l.can_edit && (
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => navigate(`/company-event/${l.event_id}/edit`)}
                  >
                    <Icon name="settings" /> {t('editEvent')}
                  </button>
                )}
                {l.can_run && (
                  <button
                    type="button"
                    className="bravos-submit"
                    onClick={() => navigate(`/host-event/${l.event_id}`)}
                  >
                    <Icon name="megaphone" /> {t('hostEvent')}
                  </button>
                )}
              </div>
            ) : (
              <p className="bravos-q__hint mt-2 mb-0">
                <Icon name="comment" className="me-1" /> {t('labToolsComingSoon')}
              </p>
            )}
          </Card>
        ))
      )}
    </Base>
  );
};

export default MyLab;
